// ShopDetailPage.tsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import {Shop} from "../../../interface/Shop";
import {getShopDetail} from "../../../api/commonApi";
import {defaultAlert} from "../../../utils/alert";

export default function ShopDetailPage() {
    const { shopId } = useParams<{ shopId: string }>();
    const navigate = useNavigate();
    const [shop, setShop] = useState<Shop | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (shopId) {
            fetchShopDetail(parseInt(shopId));
        }
    }, [shopId]);

    const fetchShopDetail = async (id: number) => {
        try {
            setLoading(true);
            const response = await getShopDetail(id);
            setShop(response.data);
        } catch (error) {
            console.error('Failed to fetch shop detail:', error);
            defaultAlert('가맹점 정보를 불러오는데 실패했습니다.');
            navigate(-1);
        } finally {
            setLoading(false);
        }
    };

    const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
        e.currentTarget.src = 'https://via.placeholder.com/400x300?text=No+Image';
    };

    const getSafeHTML = (html: string) => {
        return DOMPurify.sanitize(html, {
            ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'a', 'img', 'div', 'span'],
            ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class', 'style']
        });
    };

    if (loading) {
        return (
            <main className="container">
                <section>
                    <div style={{ textAlign: 'center', padding: '100px 20px' }}>
                        <p>로딩 중...</p>
                    </div>
                </section>
            </main>
        );
    }

    if (!shop) {
        return (
            <main className="container">
                <section>
                    <div style={{ textAlign: 'center', padding: '100px 20px' }}>
                        <p>가맹점 정보를 찾을 수 없습니다.</p>
                        <button
                            type="button"
                            className="btn confirm mt20"
                            onClick={() => navigate(-1)}
                        >
                            돌아가기
                        </button>
                    </div>
                </section>
            </main>
        );
    }

    return (
        <main className="container">
            <section className="bg-white">
                <div className="thumbnail">
                    <div className="centered">
                        <img
                            src={shop.shopImageUrl || 'https://via.placeholder.com/400x300?text=No+Image'}
                            className="landscape"
                            alt={shop.shopName}
                            onError={handleImageError}
                        />
                    </div>
                </div>
            </section>

            <section className="">
                <div className="inner-box">
                    <div className="col-info">
                        <p className="name">{shop.shopName}</p>
                        <ul className="detail-info mt10">
                            <li>
                                <span className="tit w60">주소</span>
                                <span className="txt">
                                    {shop.businessAddress}
                                    {shop.businessAddressDetail && ` ${shop.businessAddressDetail}`}
                                </span>
                            </li>
                            {shop.serviceCenterNumber && (
                                <li>
                                    <span className="tit w60">전화번호</span>
                                    <span className="txt">
                                        <a href={`tel:${shop.serviceCenterNumber}`}
                                           style={{color: '#007bff', textDecoration: 'underline'}}>
                                            {shop.serviceCenterNumber}
                                        </a>
                                    </span>
                                </li>
                            )}
                            {shop.faxNumber && (
                                <li>
                                    <span className="tit w60">팩스번호</span>
                                    <span className="txt">{shop.faxNumber}</span>
                                </li>
                            )}
                            {shop.businessCategory && (
                                <li>
                                    <span className="tit w60">업종</span>
                                    <span className="txt">{shop.businessCategory}</span>
                                </li>
                            )}
                            {shop.apparatus && (
                                <li>
                                    <span className="tit w60">시설</span>
                                    <span className="txt">{shop.apparatus}</span>
                                </li>
                            )}
                        </ul>

                        {shop.description && (
                            <div
                                className="detail-txt"
                                dangerouslySetInnerHTML={{ __html: getSafeHTML(shop.description) }}
                            />
                        )}
                    </div>
                </div>
            </section>
        </main>
    );
}